import { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { api } from '../../api/api';
import toast from 'react-hot-toast';
import {
  Box, Typography, Button, Paper, TextField, Select, MenuItem,
  FormControl, InputLabel, Stepper, Step, StepLabel, Chip, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import TvIcon from '@mui/icons-material/Tv';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import SaveIcon from '@mui/icons-material/Save';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import './quicksetup.css';

// Add 섹션 옵션
const ADD_SHOT_OPTIONS = ['Single Shot', 'Double Shot'];
const ADD_MILK_OPTIONS = ['Skim', 'Soy', 'Almond', 'Coconut', 'Oat'];
const ADD_SYRUP_OPTIONS = ['Vanilla', 'Chocolate', 'Caramel', 'Hazelnut', 'Brown Sugar', 'Lavender'];

// 시즈널 이미지 목록 (추후 DB에서 로드)
const SEASONAL_IMAGES = [
  { id: 'pistachio_2026', name: 'Pistachio Perfected', season: '2026 Spring', thumb: '/generated_menus/_test/S_real.jpg' },
  { id: 'valentine_2026', name: 'Valentine Special', season: '2026 Feb', thumb: null },
  { id: 'summer_2025', name: 'Summer Refresh', season: '2025 Summer', thumb: null },
];

const SCREEN_TYPES = [
  { key: 'E', label: 'Espresso', color: '#8B4513', desc: '에스프레소 메뉴 (아메리카노, 라떼 등)' },
  { key: 'T', label: 'Tea / Brewed', color: '#2E7D32', desc: '브루드 커피, 티, 말차 등' },
  { key: 'S', label: 'Cold / Seasonal', color: '#1565C0', desc: '초콜릿, 콜드 드링크, 시즈널' },
  { key: 'ES', label: 'Espresso + Seasonal', color: '#E65100', desc: '에스프레소 + 시즈널 합본' },
  { key: 'ET', label: 'Espresso + Tea', color: '#795548', desc: '에스프레소 + 티 합본' },
  { key: 'TS', label: 'Tea + Seasonal', color: '#00695C', desc: '티 + 시즈널 합본' },
  { key: 'P', label: 'Promotion Video', color: '#9C27B0', desc: '프로모션 영상 (4종 선택)' },
];

const TIER_OPTIONS = [
  { key: 'tier1', label: 'Tier 1 (소도시)', color: '#4CAF50' },
  { key: 'tier2', label: 'Tier 2 (중간)', color: '#FF9800' },
  { key: 'tier3', label: 'Tier 3 (대도시)', color: '#F44336' },
];

const AD_OPTIONS = [
  { key: 'full_with_apps', label: 'Full Version (앱 4종 포함)', desc: '앱 다운로드 QR + 풀 광고' },
  { key: 'full_no_apps', label: 'Full Version (앱 없음)', desc: '광고만, 앱 QR 제외' },
  { key: 'catering', label: 'Catering Version', desc: '케이터링 메뉴 포함' },
  { key: 'no_catering', label: 'Non-Catering Version', desc: '케이터링 제외 일반 버전' },
];

const S_RIGHT_OPTIONS = [
  { key: 'seasonal', label: '현재 시즈널', desc: '시즌 캠페인 이미지' },
  { key: 'rotation', label: '로테이션', desc: '기존 스크린 중 선택하여 로테이션' },
  { key: 'custom', label: '커스텀 (1~6개 제품)', desc: '기존 제품 선택하여 구성' },
];

const DEFAULT_SCREEN_PRESETS = {
  3: [
    { type: 'E', label: 'TV #1 — Espresso' },
    { type: 'T', label: 'TV #2 — Tea/Brewed' },
    { type: 'S', label: 'TV #3 — Cold/Seasonal' },
  ],
  4: [
    { type: 'E', label: 'TV #1 — Espresso' },
    { type: 'T', label: 'TV #2 — Tea/Brewed' },
    { type: 'S', label: 'TV #3 — Cold/Seasonal' },
    { type: 'P', label: 'TV #4 — Promotion Video' },
  ],
  5: [
    { type: 'E', label: 'TV #1 — Espresso' },
    { type: 'T', label: 'TV #2 — Tea/Brewed' },
    { type: 'S', label: 'TV #3 — Cold/Seasonal' },
    { type: 'S', label: 'TV #4 — Cold/Seasonal 2' },
    { type: 'P', label: 'TV #5 — Promotion Video' },
  ],
  6: [
    { type: 'E', label: 'TV #1 — Espresso' },
    { type: 'E', label: 'TV #2 — Espresso 2' },
    { type: 'T', label: 'TV #3 — Tea/Brewed' },
    { type: 'S', label: 'TV #4 — Cold/Seasonal' },
    { type: 'S', label: 'TV #5 — Cold/Seasonal 2' },
    { type: 'P', label: 'TV #6 — Promotion Video' },
  ],
  2: [
    { type: 'E', label: 'TV #1 — Espresso' },
    { type: 'S', label: 'TV #2 — Cold/Seasonal' },
  ],
};

function QuickSetup({ user }) {
  const isMobile = useMediaQuery('(max-width:700px)');

  // 기존 매장 편집 모드
  const [editMode, setEditMode] = useState(false); // false=신규, true=기존 편집
  const [existingStoreId, setExistingStoreId] = useState('');
  const [storeList, setStoreList] = useState([]);
  const [storeSearch, setStoreSearch] = useState('');
  const [loadingStores, setLoadingStores] = useState(false);

  // Step 1: 매장 정보
  const [storeName, setStoreName] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [town, setTown] = useState('');
  const [state, setState] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');

  // Step 2: TV 구성
  const [tvCount, setTvCount] = useState(3);
  const [screens, setScreens] = useState(DEFAULT_SCREEN_PRESETS[3].map((s, i) => ({
    ...s, index: i, tier: 'tier2', adType: 'full_with_apps',
    sRightOption: 'seasonal', customProducts: [], rotationScreens: [],
    notes: '', completed: false, pricePreview: null,
  })));

  // Step 3: 개별 스크린 설정
  const [activeScreen, setActiveScreen] = useState(0);
  const [step, setStep] = useState(0); // 0: 매장, 1: TV구성, 2: 스크린설정, 3: 완료
  const [tierPrices, setTierPrices] = useState(null);
  const [menuMasters, setMenuMasters] = useState([]);
  const [previewUrl, setPreviewUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [generatedPreviews, setGeneratedPreviews] = useState([]); // JPG 미리보기
  const [generatingJpg, setGeneratingJpg] = useState(false);
  // 인라인 가격 편집
  const [priceOverrides, setPriceOverrides] = useState({}); // { menuCode: { hot_s: 400, ... } }
  const [editingPrice, setEditingPrice] = useState(null); // { menuCode, sizeKey, value }
  // 드래그 메뉴 순서 변경
  const [menuOrder, setMenuOrder] = useState({}); // { screenType: [menuCode, ...] }
  const [dragItem, setDragItem] = useState(null);

  // Store 목록 로드 (기존 매장 편집용)
  const effectiveUserId = user?._id || user?.user?._id || '';
  useEffect(() => {
    if (!effectiveUserId) return;
    api.get(`/store/getall/${effectiveUserId}`).then(res => {
      const raw = res.data?.stores || res.data || [];
      const stores = Array.isArray(raw) ? raw : [];
      setStoreList(stores);
      // URL 파라미터로 storeId가 있으면 자동 로드
      const params = new URLSearchParams(window.location.search);
      const urlStoreId = params.get('storeId');
      const urlDeviceCode = params.get('deviceCode');
      if (urlDeviceCode) {
        toast(`Field kit TV code: ${urlDeviceCode} — assign this code when linking screens.`, { duration: 6000 });
      }
      if (urlStoreId) {
        setEditMode(true);
        setTimeout(() => loadExistingStore(urlStoreId), 300);
      }
    }).catch(() => {});
  }, [effectiveUserId]);

  // 기존 매장 선택 시 데이터 채우기
  const loadExistingStore = async (storeId) => {
    if (!storeId) { setEditMode(false); setExistingStoreId(''); return; }
    setLoadingStores(true);
    try {
      const store = storeList.find(s => s._id === storeId);
      if (store) {
        setEditMode(true);
        setExistingStoreId(storeId);
        setStoreName(store.name || '');
        setZipcode(store.zipcode || '');
        setTown(store.town || '');
        setState(store.state || '');
        setOwnerPhone(store.ownerPhone || '');
        setOwnerName(store.ownerName || '');
        setOwnerEmail(store.ownerEmail || '');
        setTvCount(store.screenCount || 3);
        // 기존 가격 로드
        try {
          const priceRes = await api.get(`/menu-prices/${storeId}`);
          const prices = priceRes.data?.prices || priceRes.data || [];
          const overrides = {};
          prices.forEach(p => {
            if (p.prices) overrides[p.menuCode] = p.prices;
          });
          setPriceOverrides(overrides);
        } catch (_) {}
        const preset = DEFAULT_SCREEN_PRESETS[store.screenCount || 3] || DEFAULT_SCREEN_PRESETS[3];
        setScreens(preset.map((s, i) => ({
          ...s, index: i, tier: store.pricingTier || 'tier2',
          adType: 'full_with_apps', sRightOption: 'seasonal',
          customProducts: [], rotationScreens: [], notes: '', completed: false,
        })));
        const hasPrices = Object.keys(priceOverrides).length > 0;
        toast.success(`"${store.name}" 불러옴${hasPrices ? ' (가격 데이터 있음)' : ''}`);
      }
    } catch (e) {
      toast.error('매장 정보 로드 실패');
    }
    setLoadingStores(false);
  };

  // Zip code 자동 조회
  useEffect(() => {
    const zip = zipcode.replace(/\D/g, '');
    if (zip.length === 5) {
      api.get(`/zipcode/${zip}`).then(res => {
        setTown(res.data.city || '');
        setState(res.data.stateAbbr || res.data.state || '');
        if (!storeName) setStoreName(`${res.data.stateAbbr || ''}_${(res.data.city || '').replace(/\s+/g, '')}`);
      }).catch(() => {});
    }
  }, [zipcode]);

  // 티어 가격 로드
  useEffect(() => {
    api.get('/menu-master/tier-prices').then(res => setTierPrices(res.data.tierPrices)).catch(() => {});
    api.get('/menu-master/items').then(res => setMenuMasters(res.data?.items || res.data || [])).catch(() => {});
  }, []);

  // TV 수 변경
  const handleTvCountChange = (count) => {
    setTvCount(count);
    const preset = DEFAULT_SCREEN_PRESETS[count] || DEFAULT_SCREEN_PRESETS[3];
    setScreens(preset.map((s, i) => ({
      ...s, index: i, tier: screens[0]?.tier || 'tier2',
      adType: 'full_with_apps', sRightOption: 'seasonal',
      customProducts: [], rotationScreens: [], notes: '', completed: false,
      addShots: ['Single Shot'], addMilks: ['Skim', 'Soy', 'Almond', 'Coconut', 'Oat'],
      addSyrups: ['Vanilla', 'Chocolate', 'Caramel', 'Hazelnut', 'Brown Sugar'],
      selectedSeasonals: [], videoType: 'full_with_apps',
    })));
  };

  // 스크린 업데이트
  const updateScreen = (idx, updates) => {
    setScreens(prev => prev.map((s, i) => i === idx ? { ...s, ...updates } : s));
  };

  // 미리보기 이미지 URL 생성
  const getPreviewUrl = (screenType) => {
    const base = `/generated_menus/_test/${screenType}_real.jpg`;
    return base;
  };

  // 티어별 가격 가져오기
  const getTierPrice = (menuCode, tier, sizeKey) => {
    if (!tierPrices || !tierPrices[tier]) return null;
    return tierPrices[tier][menuCode]?.[sizeKey];
  };

  // 카테고리별 메뉴 필터
  const getMenusByScreen = (screenType) => {
    const catMap = { E: 'espresso', T: ['brewed_coffee', 'tea'], S: ['chocolate', 'cold_drink'] };
    const cats = Array.isArray(catMap[screenType]) ? catMap[screenType] : [catMap[screenType]];
    return menuMasters.filter(m => cats.includes(m.category));
  };

  // 가격 오버라이드 적용
  const getPrice = (menuCode, tier, tierKey) => {
    const override = priceOverrides[menuCode]?.[tierKey];
    if (override !== undefined) return override;
    return tierPrices?.[tier]?.[menuCode]?.[tierKey] || null;
  };

  const handlePriceEdit = (menuCode, tierKey, dollarValue) => {
    const cents = Math.round(parseFloat(dollarValue || 0) * 100);
    if (cents <= 0) return;
    setPriceOverrides(prev => ({
      ...prev,
      [menuCode]: { ...(prev[menuCode] || {}), [tierKey]: cents },
    }));
  };

  // 메뉴 순서 (드래그)
  const getOrderedMenus = (screenType) => {
    const menus = getMenusByScreen(screenType);
    const order = menuOrder[screenType];
    if (!order) return menus;
    return [...menus].sort((a, b) => {
      const ai = order.indexOf(a.code);
      const bi = order.indexOf(b.code);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  };

  const handleMenuDragStart = (code) => setDragItem(code);
  const handleMenuDragOver = (e, targetCode, screenType) => {
    e.preventDefault();
    if (!dragItem || dragItem === targetCode) return;
    const menus = getOrderedMenus(screenType).map(m => m.code);
    const fromIdx = menus.indexOf(dragItem);
    const toIdx = menus.indexOf(targetCode);
    if (fromIdx === -1 || toIdx === -1) return;
    const newOrder = [...menus];
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, dragItem);
    setMenuOrder(prev => ({ ...prev, [screenType]: newOrder }));
  };
  const handleMenuDragEnd = () => setDragItem(null);

  // 포맷
  const fmt = (cents) => cents != null ? `$${(cents / 100).toFixed(2)}` : '-';
  const fmtPhone = (val) => {
    const d = val.replace(/\D/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };

  // 저장
  const handleSave = async () => {
    setSaving(true);
    try {
      let storeId = existingStoreId;

      if (editMode && existingStoreId) {
        // 기존 매장 업데이트
        await api.patch(`/store/update/${existingStoreId}`, {
          name: storeName, town, state, zipcode, ownerPhone, ownerName, ownerEmail,
          screenCount: tvCount, pricingTier: screens[0]?.tier || 'tier2',
        });

        // 가격 업데이트 (오버라이드된 것만)
        if (Object.keys(priceOverrides).length > 0) {
          const prices = Object.entries(priceOverrides).map(([menuCode, prices]) => ({
            menuCode, prices,
          }));
          await api.post(`/menu-prices/bulk-update/${existingStoreId}`, {
            prices, userId: user?._id, reason: 'Quick Setup 기존 매장 수정',
          });
        }
      } else {
        // 신규 스토어 생성
        const storeRes = await api.post('/quick-setup/create', {
          storeName, town, state, zipcode, ownerPhone, ownerName, ownerEmail,
          tvCount, tier: screens[0]?.tier || 'tier2',
          actorName: user?.username || '',
          screens: screens.map((s, i) => ({
            index: i + 1, type: s.type, tier: s.tier,
            adType: s.adType, videoType: s.videoType,
            sRightOption: s.sRightOption,
            selectedSeasonals: s.selectedSeasonals,
            customProducts: s.customProducts,
            addShots: s.addShots, addMilks: s.addMilks, addSyrups: s.addSyrups,
            notes: s.notes,
          })),
          priceOverrides,
          menuOrder,
        });
        storeId = storeRes.data?.store?._id;
        if (!storeId) throw new Error('Store creation failed');

        // 티어 기반 가격 초기화
        await api.post(`/menu-prices/init-tier/${storeId}`, { tier: screens[0]?.tier || 'tier2' });
      }

      // 스크린 설정 저장
      await api.put(`/screen-config/${storeId}`, {
        screens: screens.map((s, i) => ({
          screenIndex: i + 1,
          screenName: s.label,
          displayType: s.type,
        })),
        layout: tvCount <= 2 ? 'combined_2' : tvCount === 3 ? 'standard_3' : 'standard_4',
      });

      toast.success(`매장 "${storeName}" ${editMode ? '수정' : '설정'} 완료!`);
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || '저장 실패');
    }
    setSaving(false);
  };

  const currentScreen = screens[activeScreen];
  const allCompleted = screens.every(s => s.completed);
  const isManager = user?.role === 'tlj_manager' || user?.role === 'tlj_admin' || user?.role === 'admin';
  const isOwner = user?.role === 'store_owner' || user?.role === 'store_manager';

  return (
    <Layout title="Quick Setup" user={user}>
      <Box className="qs-root">
        {/* Stepper */}
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
          {['매장 정보', 'TV 구성', '스크린 설정', '완료'].map((label, i) => (
            <Step key={label} completed={step > i}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* ═══ Step 0: 매장 정보 ═══ */}
        {step === 0 && (
          <Paper className="qs-card">
            {/* 신규/기존 전환 */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip label="신규 매장" variant={editMode ? 'outlined' : 'filled'}
                onClick={() => { setEditMode(false); setExistingStoreId(''); setStoreName(''); setZipcode(''); setTown(''); setState(''); setOwnerName(''); setOwnerPhone(''); setOwnerEmail(''); setPriceOverrides({}); }}
                sx={{ cursor: 'pointer', fontWeight: 700, ...(editMode ? {} : { background: '#551FFF', color: '#fff' }) }} />
              <Chip label="기존 매장 수정" variant={editMode ? 'filled' : 'outlined'}
                onClick={() => setEditMode(true)}
                sx={{ cursor: 'pointer', fontWeight: 700, ...(editMode ? { background: '#22c55e', color: '#fff' } : {}) }} />
            </Box>

            {editMode && (
              <Box sx={{ mb: 2 }}>
                <TextField fullWidth size="small" label="매장 검색..." value={storeSearch}
                  onChange={e => setStoreSearch(e.target.value)}
                  sx={{ mb: 1, '& .MuiInputBase-root': { fontSize: 13 } }} />
                <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 2 }}>
                  {storeList
                    .filter(s => !storeSearch || (s.name || '').toLowerCase().includes(storeSearch.toLowerCase()) || (s.ownerName || '').toLowerCase().includes(storeSearch.toLowerCase()))
                    .slice(0, 30)
                    .map(s => (
                      <Box key={s._id} onClick={() => loadExistingStore(s._id)}
                        sx={{
                          px: 2, py: 1, cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f5f5f5',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          background: existingStoreId === s._id ? 'rgba(34,197,94,0.08)' : 'white',
                          '&:hover': { background: 'rgba(85,31,255,0.04)' },
                        }}>
                        <Box>
                          <span style={{ fontWeight: 600 }}>{s.name}</span>
                          <span style={{ color: '#888', marginLeft: 8, fontSize: 11 }}>{s.ownerName || ''}</span>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {!s.zipcode && <Chip label="주소없음" size="small" color="warning" variant="outlined" sx={{ fontSize: 9, height: 18 }} />}
                          <Chip label={`${s.screenCount || 0}TV`} size="small" variant="outlined" sx={{ fontSize: 9, height: 18 }} />
                        </Box>
                      </Box>
                    ))}
                </Box>
              </Box>
            )}

            <Typography className="qs-card-title">{editMode ? '매장 정보 수정' : '매장 정보 입력'}</Typography>
            <Typography sx={{ fontSize: 13, color: '#888', mb: 2 }}>
              {editMode ? '기존 매장 정보를 수정합니다' : '점주님과 미팅 시 매장 기본 정보를 입력합니다'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Owner Name" value={ownerName}
                  onChange={e => setOwnerName(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Phone" value={ownerPhone}
                  onChange={e => setOwnerPhone(fmtPhone(e.target.value))} inputProps={{ inputMode: 'tel' }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Owner Email" type="email" value={ownerEmail}
                  onChange={e => setOwnerEmail(e.target.value)} placeholder="owner@email.com" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Zip Code" value={zipcode}
                  onChange={e => setZipcode(e.target.value)} inputProps={{ maxLength: 5, inputMode: 'numeric' }} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" label="City" value={town} InputProps={{ readOnly: true }}
                  sx={{ '& input': { background: '#f9fafb' } }} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" label="State" value={state} InputProps={{ readOnly: true }}
                  sx={{ '& input': { background: '#f9fafb' } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Store Name *" value={storeName}
                  onChange={e => setStoreName(e.target.value)} />
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" endIcon={<NavigateNextIcon />}
                disabled={!storeName || !zipcode}
                onClick={() => setStep(1)}
                sx={{ background: '#551FFF', borderRadius: 2, px: 4 }}>
                다음
              </Button>
              {editMode && existingStoreId && Object.keys(priceOverrides).length > 0 && (
                <Button variant="contained" endIcon={<NavigateNextIcon />}
                  onClick={() => { setStep(2); setActiveScreen(0); }}
                  sx={{ background: '#22c55e', borderRadius: 2, px: 3, ml: 1 }}>
                  바로 가격 수정 →
                </Button>
              )}
            </Box>
          </Paper>
        )}

        {/* ═══ Step 1: TV 구성 ═══ */}
        {step === 1 && (
          <Paper className="qs-card">
            <Typography className="qs-card-title">TV 구성 — {storeName}</Typography>
            <Typography sx={{ fontSize: 13, color: '#888', mb: 3 }}>
              매장에 설치할 TV 수를 선택하세요
            </Typography>

            {/* TV 수 선택 */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 3 }}>
              <IconButton onClick={() => tvCount > 1 && handleTvCountChange(tvCount - 1)}
                sx={{ background: '#f5f5f5', '&:hover': { background: '#e0e0e0' } }}>
                <RemoveIcon />
              </IconButton>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1.5, background: '#f8f7ff', borderRadius: 3, border: '2px solid #551FFF' }}>
                <TvIcon sx={{ color: '#551FFF', fontSize: 28 }} />
                <Typography sx={{ fontSize: 32, fontWeight: 800, color: '#551FFF' }}>{tvCount}</Typography>
                <Typography sx={{ fontSize: 14, color: '#888' }}>대</Typography>
              </Box>
              <IconButton onClick={() => tvCount < 6 && handleTvCountChange(tvCount + 1)}
                sx={{ background: '#f5f5f5', '&:hover': { background: '#e0e0e0' } }}>
                <AddIcon />
              </IconButton>
            </Box>

            {/* 스크린 리스트 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {screens.map((s, i) => (
                <Box key={i} sx={{
                  display: 'flex', alignItems: 'center', gap: 2, p: 2,
                  borderRadius: 2, border: '1.5px solid #e5e7eb', flexWrap: 'wrap',
                }}>
                  <Chip label={`TV #${i + 1}`} sx={{ fontWeight: 700, background: '#f8f7ff', color: '#551FFF' }} />

                  {/* 화면 타입 선택 */}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>화면 타입</InputLabel>
                    <Select value={s.type} label="화면 타입"
                      onChange={e => updateScreen(i, { type: e.target.value, label: `TV #${i + 1} — ${SCREEN_TYPES.find(t => t.key === e.target.value)?.label}` })}>
                      {SCREEN_TYPES.map(t => (
                        <MenuItem key={t.key} value={t.key}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: t.color }} />
                            {t.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* 티어 선택 */}
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>가격 티어</InputLabel>
                    <Select value={s.tier} label="가격 티어"
                      onChange={e => updateScreen(i, { tier: e.target.value })}>
                      {TIER_OPTIONS.map(t => (
                        <MenuItem key={t.key} value={t.key}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', background: t.color }} />
                            {t.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Typography sx={{ fontSize: 12, color: '#888', flex: 1 }}>
                    {SCREEN_TYPES.find(t => t.key === s.type)?.desc}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, flexWrap: 'wrap', gap: 1 }}>
              <Button startIcon={<NavigateBeforeIcon />} onClick={() => setStep(0)} sx={{ color: '#888' }}>이전</Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {editMode && (
                  <Button variant="outlined" endIcon={<NavigateNextIcon />}
                    onClick={() => { setStep(2); setActiveScreen(0); }}
                    sx={{ borderColor: '#22c55e', color: '#22c55e', borderRadius: 2, px: 3 }}>
                    바로 가격 설정 →
                  </Button>
                )}
                <Button variant="contained" endIcon={<NavigateNextIcon />}
                  onClick={() => { setStep(2); setActiveScreen(0); }}
                  sx={{ background: '#551FFF', borderRadius: 2, px: 4 }}>
                  스크린 설정 시작
                </Button>
              </Box>
            </Box>
          </Paper>
        )}

        {/* ═══ Step 2: 스크린별 상세 설정 ═══ */}
        {step === 2 && currentScreen && (
          <>
            {/* 스크린 탭 */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, overflowX: 'auto', pb: 0.5 }}>
              {screens.map((s, i) => (
                <Chip key={i} label={`TV #${i + 1} ${s.type}`}
                  icon={s.completed ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : undefined}
                  onClick={() => setActiveScreen(i)}
                  sx={{
                    fontWeight: 600, cursor: 'pointer', flexShrink: 0,
                    background: activeScreen === i ? '#551FFF' : s.completed ? '#e8f5e9' : '#f5f5f5',
                    color: activeScreen === i ? '#fff' : s.completed ? '#2e7d32' : '#555',
                  }} />
              ))}
            </Box>

            <Paper className="qs-card">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography className="qs-card-title" sx={{ mb: 0 }}>
                  TV #{activeScreen + 1} — {SCREEN_TYPES.find(t => t.key === currentScreen.type)?.label}
                </Typography>
                <Chip label={TIER_OPTIONS.find(t => t.key === currentScreen.tier)?.label}
                  sx={{ fontWeight: 600, background: TIER_OPTIONS.find(t => t.key === currentScreen.tier)?.color, color: '#fff' }} />
              </Box>

              {/* 미리보기 이미지 */}
              <Box sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#000' }}>
                <img
                  src={getPreviewUrl(currentScreen.type)}
                  alt={`${currentScreen.type} preview`}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </Box>

              {/* 가격 테이블 (인라인 편집 + 드래그 순서 변경) */}
              {currentScreen.type !== 'P' && (
              <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#333' }}>
                  가격 설정 ({TIER_OPTIONS.find(t => t.key === currentScreen.tier)?.label})
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#888' }}>
                  클릭하여 가격 수정 · 드래그하여 순서 변경
                </Typography>
              </Box>
              <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px 4px', width: 24 }}></th>
                      <th style={{ padding: '8px 12px', textAlign: 'left' }}>Menu</th>
                      <th style={{ padding: '8px', textAlign: 'center', color: '#C62828', fontSize: 10 }}>HOT 12oz</th>
                      <th style={{ padding: '8px', textAlign: 'center', color: '#C62828', fontSize: 10 }}>HOT 16oz</th>
                      <th style={{ padding: '8px', textAlign: 'center', color: '#1565C0', fontSize: 10 }}>ICE 16oz</th>
                      <th style={{ padding: '8px', textAlign: 'center', color: '#1565C0', fontSize: 10 }}>ICE 20oz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getOrderedMenus(currentScreen.type).map(m => (
                      <tr key={m.code}
                        draggable
                        onDragStart={() => handleMenuDragStart(m.code)}
                        onDragOver={e => handleMenuDragOver(e, m.code, currentScreen.type)}
                        onDragEnd={handleMenuDragEnd}
                        style={{
                          borderBottom: '1px solid #f0f0f0',
                          cursor: 'grab',
                          background: dragItem === m.code ? '#f0f0ff' : priceOverrides[m.code] ? '#fffde7' : '#fff',
                        }}>
                        <td style={{ textAlign: 'center', color: '#ccc', padding: '4px' }}>≡</td>
                        <td style={{ padding: '6px 12px', fontWeight: 500, fontSize: 12 }}>
                          {m.nameEn || m.name}
                          {priceOverrides[m.code] && <span style={{ fontSize: 8, color: '#C5A55A', marginLeft: 4 }}>edited</span>}
                        </td>
                        {['hot_12', 'hot_16', 'ice_16', 'ice_20'].map(tierKey => {
                          const price = getPrice(m.code, currentScreen.tier, tierKey);
                          const isOverridden = priceOverrides[m.code]?.[tierKey] !== undefined;
                          const isEditing = editingPrice?.menuCode === m.code && editingPrice?.sizeKey === tierKey;
                          return (
                            <td key={tierKey} style={{ padding: '3px', textAlign: 'center' }}>
                              {isEditing ? (
                                <input type="number" step="0.05" autoFocus
                                  defaultValue={price ? (price / 100).toFixed(2) : ''}
                                  onBlur={e => { handlePriceEdit(m.code, tierKey, e.target.value); setEditingPrice(null); }}
                                  onKeyDown={e => { if (e.key === 'Enter') { handlePriceEdit(m.code, tierKey, e.target.value); setEditingPrice(null); } if (e.key === 'Escape') setEditingPrice(null); }}
                                  style={{
                                    width: 58, padding: '5px 3px', border: '2px solid #551FFF', borderRadius: 4,
                                    textAlign: 'center', fontSize: 13, fontWeight: 700, outline: 'none',
                                  }} />
                              ) : (
                                <Box onClick={() => price && setEditingPrice({ menuCode: m.code, sizeKey: tierKey })}
                                  sx={{
                                    cursor: price ? 'pointer' : 'default', py: 0.5, borderRadius: 1,
                                    background: isOverridden ? '#fef3c7' : 'transparent',
                                    '&:hover': price ? { background: '#f0f0ff' } : {},
                                  }}>
                                  {price ? (
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>{fmt(price)}</span>
                                  ) : (
                                    <span style={{ color: '#ddd' }}>—</span>
                                  )}
                                </Box>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
              {Object.keys(priceOverrides).length > 0 && (
                <Chip size="small" label={`${Object.keys(priceOverrides).length}개 메뉴 가격 수정됨`}
                  sx={{ mb: 1, background: '#fef3c7', color: '#92400e', fontWeight: 600 }} />
              )}
              </>
              )}

              {/* ── E 화면: Add 섹션 (샷/밀크/시럽 체크박스) ── */}
              {currentScreen.type === 'E' && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1, color: '#333' }}>
                    Add 섹션 옵션
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    {/* Espresso Shot */}
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#8B4513', mb: 0.5 }}>ESPRESSO *Add shot</Typography>
                    <FormGroup row sx={{ mb: 1 }}>
                      {ADD_SHOT_OPTIONS.map(opt => (
                        <FormControlLabel key={opt} sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                          control={<Checkbox size="small" checked={currentScreen.addShots?.includes(opt)}
                            onChange={e => {
                              const shots = e.target.checked
                                ? [...(currentScreen.addShots || []), opt]
                                : (currentScreen.addShots || []).filter(s => s !== opt);
                              updateScreen(activeScreen, { addShots: shots });
                            }} />}
                          label={opt} />
                      ))}
                    </FormGroup>

                    {/* Milk */}
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#8B4513', mb: 0.5 }}>MILK</Typography>
                    <FormGroup row sx={{ mb: 1 }}>
                      {ADD_MILK_OPTIONS.map(opt => (
                        <FormControlLabel key={opt} sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                          control={<Checkbox size="small" checked={currentScreen.addMilks?.includes(opt)}
                            onChange={e => {
                              const milks = e.target.checked
                                ? [...(currentScreen.addMilks || []), opt]
                                : (currentScreen.addMilks || []).filter(s => s !== opt);
                              updateScreen(activeScreen, { addMilks: milks });
                            }} />}
                          label={opt} />
                      ))}
                    </FormGroup>

                    {/* Syrup */}
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#8B4513', mb: 0.5 }}>SYRUP</Typography>
                    <FormGroup row>
                      {ADD_SYRUP_OPTIONS.map(opt => (
                        <FormControlLabel key={opt} sx={{ '& .MuiTypography-root': { fontSize: 12 } }}
                          control={<Checkbox size="small" checked={currentScreen.addSyrups?.includes(opt)}
                            onChange={e => {
                              const syrups = e.target.checked
                                ? [...(currentScreen.addSyrups || []), opt]
                                : (currentScreen.addSyrups || []).filter(s => s !== opt);
                              updateScreen(activeScreen, { addSyrups: syrups });
                            }} />}
                          label={opt} />
                      ))}
                    </FormGroup>
                  </Paper>
                </Box>
              )}

              {/* ── S 화면: 오른쪽 영역 + 시즈널 이미지 리스트 ── */}
              {currentScreen.type === 'S' && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1, color: '#333' }}>
                    S 화면 오른쪽 영역
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    {S_RIGHT_OPTIONS.map(opt => (
                      <Chip key={opt.key} label={opt.label}
                        onClick={() => updateScreen(activeScreen, { sRightOption: opt.key })}
                        sx={{
                          cursor: 'pointer', fontWeight: 600,
                          background: currentScreen.sRightOption === opt.key ? '#551FFF' : '#f5f5f5',
                          color: currentScreen.sRightOption === opt.key ? '#fff' : '#555',
                        }} />
                    ))}
                  </Box>

                  {/* 시즈널 이미지 리스트 (여러 개 선택 → 로테이션) */}
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}>
                      시즈널 이미지 선택 (복수 선택 시 자동 로테이션)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {SEASONAL_IMAGES.map(img => {
                        const selected = (currentScreen.selectedSeasonals || []).includes(img.id);
                        return (
                          <Box key={img.id} onClick={() => {
                            const prev = currentScreen.selectedSeasonals || [];
                            const next = selected ? prev.filter(id => id !== img.id) : [...prev, img.id];
                            updateScreen(activeScreen, { selectedSeasonals: next });
                          }} sx={{
                            width: isMobile ? 80 : 120, cursor: 'pointer', textAlign: 'center',
                            border: `2px solid ${selected ? '#551FFF' : '#e5e7eb'}`,
                            borderRadius: 2, overflow: 'hidden', transition: '0.15s',
                            background: selected ? '#f8f7ff' : '#fff',
                          }}>
                            <Box sx={{ height: isMobile ? 50 : 70, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {img.thumb ? (
                                <img src={img.thumb} alt={img.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <Typography sx={{ fontSize: 10, color: '#aaa' }}>No Image</Typography>
                              )}
                            </Box>
                            <Box sx={{ p: 0.5 }}>
                              <Typography sx={{ fontSize: 9, fontWeight: 600, lineHeight: 1.2 }}>{img.name}</Typography>
                              <Typography sx={{ fontSize: 8, color: '#888' }}>{img.season}</Typography>
                            </Box>
                            {selected && <Box sx={{ background: '#551FFF', color: '#fff', fontSize: 9, py: 0.2, fontWeight: 700 }}>Selected</Box>}
                          </Box>
                        );
                      })}
                    </Box>
                    {(currentScreen.selectedSeasonals || []).length > 1 && (
                      <Chip size="small" label={`${currentScreen.selectedSeasonals.length}개 선택 → 자동 로테이션`}
                        sx={{ mt: 1, background: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: 11 }} />
                    )}
                  </Paper>

                  {currentScreen.sRightOption === 'custom' && (
                    <Paper variant="outlined" sx={{ mt: 1.5, p: 1.5, borderRadius: 2, background: '#f8f7ff' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>커스텀 제품 선택 (1~6개)</Typography>
                      <Typography sx={{ fontSize: 11, color: '#888', mb: 1 }}>
                        시즈널 이미지를 개별 선택하여 커스텀 구성합니다. 메모에 원하는 제품명을 적어주세요.
                      </Typography>
                      <TextField fullWidth size="small" multiline rows={2}
                        placeholder="예: Pistachio Cloud Matcha, Pistachio Cloud Latte, Strawberry Latte..."
                        value={currentScreen.customProducts?.join?.(', ') || ''}
                        onChange={e => updateScreen(activeScreen, { customProducts: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
                    </Paper>
                  )}
                </Box>
              )}

              {/* ── P 화면: 프로모션 비디오 선택 ── */}
              {currentScreen.type === 'P' && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1, color: '#9C27B0' }}>
                    <PlayCircleIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                    프로모션 비디오 선택
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {AD_OPTIONS.map(a => {
                      const selected = currentScreen.videoType === a.key;
                      return (
                        <Paper key={a.key} variant="outlined"
                          onClick={() => updateScreen(activeScreen, { videoType: a.key })}
                          sx={{
                            p: 2, borderRadius: 2, cursor: 'pointer', transition: '0.15s',
                            border: `2px solid ${selected ? '#9C27B0' : '#e5e7eb'}`,
                            background: selected ? '#faf5ff' : '#fff',
                          }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Checkbox checked={selected} sx={{ p: 0, color: '#9C27B0', '&.Mui-checked': { color: '#9C27B0' } }} />
                            <Box>
                              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{a.label}</Typography>
                              <Typography sx={{ fontSize: 12, color: '#888' }}>{a.desc}</Typography>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                  <Typography sx={{ fontSize: 11, color: '#888', mt: 1 }}>
                    영상은 추후 업로드 예정입니다. 선택한 타입을 기록합니다.
                  </Typography>
                </Box>
              )}

              {/* 메모 */}
              <Box sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1, color: '#333' }}>
                  메모 / 특이사항
                </Typography>
                <TextField fullWidth multiline rows={3} size="small"
                  placeholder="가격 변경 요청, 커스텀 제품, 특이사항 등..."
                  value={currentScreen.notes}
                  onChange={e => updateScreen(activeScreen, { notes: e.target.value })} />
              </Box>

              {/* 스크린 완료 버튼 */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
                <Button startIcon={<NavigateBeforeIcon />}
                  disabled={activeScreen === 0}
                  onClick={() => setActiveScreen(prev => prev - 1)}
                  sx={{ color: '#888' }}>이전 TV</Button>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant={currentScreen.completed ? 'outlined' : 'contained'}
                    startIcon={<CheckCircleIcon />}
                    onClick={() => updateScreen(activeScreen, { completed: !currentScreen.completed })}
                    sx={{
                      background: currentScreen.completed ? undefined : '#22c55e',
                      borderColor: currentScreen.completed ? '#22c55e' : undefined,
                      color: currentScreen.completed ? '#22c55e' : '#fff',
                      borderRadius: 2,
                    }}>
                    {currentScreen.completed ? '완료됨' : '이 스크린 완료'}
                  </Button>

                  {activeScreen < screens.length - 1 ? (
                    <Button variant="contained" endIcon={<NavigateNextIcon />}
                      onClick={() => setActiveScreen(prev => prev + 1)}
                      sx={{ background: '#551FFF', borderRadius: 2 }}>다음 TV</Button>
                  ) : (
                    <Button variant="contained" endIcon={<SaveIcon />}
                      disabled={!allCompleted || saving}
                      onClick={handleSave}
                      sx={{ background: allCompleted ? (isOwner ? '#f59e0b' : '#551FFF') : '#ccc', borderRadius: 2 }}>
                      {saving ? '처리 중...' : isOwner ? '변경 요청 제출' : '전체 저장'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Paper>

            {/* 전체 요약 (하단) */}
            <Paper sx={{ p: 2, borderRadius: 3, mt: 2, background: '#fafafa' }}>
              <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 1 }}>설정 요약</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 12, color: '#555' }}>매장: <strong>{storeName}</strong></Typography>
                <Typography sx={{ fontSize: 12, color: '#555' }}>TV: <strong>{tvCount}대</strong></Typography>
                <Typography sx={{ fontSize: 12, color: '#555' }}>
                  완료: <strong>{screens.filter(s => s.completed).length}/{screens.length}</strong>
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {screens.map((s, i) => (
                  <Chip key={i} size="small"
                    label={`#${i + 1} ${s.type} ${s.completed ? '✓' : ''}`}
                    sx={{
                      fontSize: 11, fontWeight: 600,
                      background: s.completed ? '#e8f5e9' : '#fff',
                      border: `1px solid ${s.completed ? '#22c55e' : '#ddd'}`,
                    }} />
                ))}
              </Box>
            </Paper>
          </>
        )}

        {/* ═══ Step 3: 완료 + 미리보기 ═══ */}
        {step === 3 && (
          <Paper className="qs-card">
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 1 }} />
              <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 0.5 }}>
                {editMode ? '수정 완료!' : '설정 완료!'}
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#888' }}>
                {storeName} — TV {tvCount}대 설정이 저장되었습니다.
              </Typography>
            </Box>

            {/* 설정 요약 */}
            <Box sx={{ background: '#f8f7ff', borderRadius: 2, p: 2, mb: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#551FFF', mb: 1 }}>설정 요약</Typography>
              {screens.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #eee', fontSize: 13 }}>
                  <span>TV #{i + 1} — <strong>{s.type}</strong> ({TIER_OPTIONS.find(t => t.key === s.tier)?.label})</span>
                  <span style={{ color: '#888' }}>{s.type === 'P' ? AD_OPTIONS.find(a => a.key === s.adType)?.label : SCREEN_TYPES.find(t => t.key === s.type)?.label}</span>
                </Box>
              ))}
              {ownerEmail && (
                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #eee', fontSize: 12, color: '#888' }}>
                  Owner: {ownerName} · {ownerEmail}
                </Box>
              )}
            </Box>

            {/* 메뉴보드 JPG 미리보기 / 생성 */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>메뉴보드 미리보기</Typography>
                <Button size="small" variant="contained" disabled={generatingJpg}
                  onClick={async () => {
                    const storeId = existingStoreId;
                    if (!storeId) { toast.error('Store ID가 없습니다. 매장 목록에서 JPG를 생성하세요.'); return; }
                    setGeneratingJpg(true);
                    try {
                      await api.post(`/svg/generate/${storeId}`);
                      const res = await api.get(`/svg/generated/${storeId}`);
                      setGeneratedPreviews(res.data?.files || []);
                      toast.success('메뉴보드 JPG 생성 완료!');
                    } catch (e) {
                      toast.error('생성 실패: ' + (e.response?.data?.message || e.message));
                    }
                    setGeneratingJpg(false);
                  }}
                  sx={{ fontSize: 11, background: '#551FFF', borderRadius: 2 }}>
                  {generatingJpg ? '생성 중...' : 'JPG 생성'}
                </Button>
              </Box>

              {generatedPreviews.length > 0 ? (
                <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 1 }}>
                  {generatedPreviews.map((img, i) => {
                    const url = img.url || `/generated_menus/${storeName}/${typeof img === 'string' ? img : img.name}`;
                    return (
                      <Box key={i} sx={{ flexShrink: 0, textAlign: 'center' }}>
                        <a href={url} target="_blank" rel="noreferrer">
                          <img src={url} alt={typeof img === 'string' ? img : img.name}
                            style={{ width: 240, height: 135, objectFit: 'cover', borderRadius: 8, border: '2px solid #e5e7eb', cursor: 'zoom-in' }}
                            onError={e => { e.target.src = '/generated_menus/_test/E_real.jpg'; }} />
                        </a>
                        <Typography sx={{ fontSize: 10, color: '#888', mt: 0.5 }}>
                          {typeof img === 'string' ? img : img.name}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3, border: '2px dashed #e5e7eb', borderRadius: 2, color: '#aaa', fontSize: 13 }}>
                  {existingStoreId
                    ? 'JPG 생성 버튼을 클릭하여 메뉴보드 이미지를 확인하세요'
                    : '신규 매장은 Store 목록에서 가격 설정 후 JPG를 생성할 수 있습니다'}
                </Box>
              )}
            </Box>

            {/* 액션 버튼 */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={() => {
                setStep(0); setEditMode(false); setExistingStoreId('');
                setScreens(DEFAULT_SCREEN_PRESETS[3].map((s, i) => ({ ...s, index: i, tier: 'tier2', adType: 'full_with_apps', sRightOption: 'seasonal', customProducts: [], rotationScreens: [], notes: '', completed: false })));
                setStoreName(''); setZipcode(''); setOwnerEmail(''); setOwnerName(''); setOwnerPhone('');
                setPriceOverrides({}); setGeneratedPreviews([]);
              }} sx={{ background: '#551FFF', borderRadius: 2 }}>새 매장 설정</Button>
              <Button variant="outlined" onClick={() => window.location.href = '/store'}
                sx={{ borderRadius: 2, color: '#551FFF', borderColor: '#551FFF' }}>매장 목록</Button>
              {existingStoreId && (
                <Button variant="outlined" onClick={() => window.location.href = `/pricing/${existingStoreId}`}
                  sx={{ borderRadius: 2, color: '#1B5E3B', borderColor: '#1B5E3B' }}>가격 상세 편집</Button>
              )}
            </Box>
          </Paper>
        )}
      </Box>
    </Layout>
  );
}

export default QuickSetup;
